import * as React from 'react';
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';

/**
 * VoidTransactionDialog - Dialog for voiding a transaction with reason input
 * 
 * Voiding creates a reversing entry to maintain audit trail while correcting the balance.
 * 
 * Props:
 * - open: boolean - Controls visibility
 * - onOpenChange: (open: boolean) => void - Callback for open state changes
 * - transaction: object - The transaction to void
 * - onConfirm: (reason: string) => Promise<void> - Callback when void is confirmed
 * - isLoading: boolean - Whether the void operation is in progress
 */
const VoidTransactionDialog = ({
  open,
  onOpenChange,
  transaction,
  onConfirm,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for voiding this transaction');
      return;
    }
    setError('');
    await onConfirm(reason.trim());
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen && !isLoading) {
      setReason('');
      setError('');
    }
    onOpenChange(newOpen);
  };

  if (!transaction) return null;

  // Determine transaction details
  const hasInvoicePayment = !!transaction.linked_invoice_payment;
  const hasPurchasePayment = !!transaction.linked_purchase_payment;
  const hasTransfer = !!transaction.linked_transfer;

  const getTitle = () => {
    if (hasInvoicePayment) return 'Void Invoice Payment';
    if (hasPurchasePayment) return 'Void Purchase Payment';
    if (hasTransfer) return 'Void Transfer';
    return 'Void Transaction';
  };

  const getDescription = () => {
    if (hasInvoicePayment) {
      const invoiceNum = transaction.linked_invoice_payment.invoice.invoice_num;
      return `Are you sure you want to void this payment for Invoice #${invoiceNum}?`;
    }
    if (hasPurchasePayment) {
      const purchaseNum = transaction.linked_purchase_payment.purchase.purchase_num;
      return `Are you sure you want to void this payment for Purchase #${purchaseNum}?`;
    }
    if (hasTransfer) {
      return 'Are you sure you want to void this transfer?';
    }
    return 'Are you sure you want to void this transaction?';
  };

  // Build explanation of what will happen
  const getImpactExplanation = () => {
    const items = [];

    items.push({
      text: `Mark the original ${formatCurrency(transaction.amount)} transaction as voided`,
    });

    items.push({
      text: `Create a reversing ${transaction.transaction_type === 'deposit' ? 'withdrawal' : 'deposit'} transaction`,
    });

    if (hasInvoicePayment) {
      items.push({
        text: `Remove the payment from Invoice #${transaction.linked_invoice_payment.invoice.invoice_num}`,
      });
      items.push({
        text: 'Update invoice status and balance due',
      });
    }

    if (hasPurchasePayment) {
      items.push({
        text: `Remove the payment from Purchase #${transaction.linked_purchase_payment.purchase.purchase_num}`,
      });
      items.push({
        text: 'Update purchase status and balance due',
      });
    }

    if (hasTransfer) {
      items.push({
        text: 'Void the paired transfer transaction on the other account',
      });
    }

    items.push({
      text: 'Bank account balance will be corrected automatically',
    });

    return items;
  };

  const impactItems = getImpactExplanation();

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-popover)] rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
            <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-[var(--ds-text-primary)]">
              <AlertTriangle className="h-5 w-5 text-[var(--ds-status-warning)]" />
              {getTitle()}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-hover-bg)] rounded-md transition-colors"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>
          
          {/* Content */}
          <div className="px-6 py-5 space-y-4">
            {/* Description */}
            <p className="text-[var(--ds-text-primary)]">
              {getDescription()}
            </p>

            {/* Transaction Summary */}
            <div className="bg-[var(--ds-surface-panel2)] rounded-[var(--ds-radius-md)] p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--ds-text-muted)]">Amount</span>
                <span className={`font-medium tabular-nums ${
                  transaction.transaction_type === 'deposit'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {transaction.transaction_type === 'deposit' ? '+' : '-'}
                  {formatCurrency(transaction.amount)}
                </span>
              </div>
              {transaction.description && (
                <div className="mt-2 text-sm text-[var(--ds-text-secondary)]">
                  {transaction.description}
                </div>
              )}
            </div>

            {/* Impact Warning Box */}
            <div className="bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)]/30 rounded-[var(--ds-radius-md)] p-4">
              <h4 className="text-sm font-medium text-[var(--ds-text-primary)] mb-3">
                This will:
              </h4>
              <ul className="space-y-2">
                {impactItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-[var(--ds-status-warning)] mt-0.5">•</span>
                    <span className="text-[var(--ds-text-secondary)]">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Reason Input */}
            <div>
              <label className="block text-sm font-medium text-[var(--ds-text-primary)] mb-2">
                Reason for voiding <span className="text-[var(--ds-status-danger)]">*</span>
              </label>
              <Textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Enter the reason for voiding this transaction..."
                rows={3}
                disabled={isLoading}
                className="w-full"
              />
              {error && (
                <p className="mt-1 text-sm text-[var(--ds-status-danger)]">
                  {error}
                </p>
              )}
            </div>

            {/* Audit Note */}
            <p className="text-xs text-[var(--ds-text-muted)]">
              Voided transactions are preserved for audit purposes and cannot be deleted. 
              A reversing transaction will be created to correct the account balance.
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[var(--ds-border-divider)] flex items-center justify-end gap-3 bg-[var(--ds-surface-panel2)]">
            <Button
              variant="secondary"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isLoading || !reason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Voiding...
                </>
              ) : (
                'Void Transaction'
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default VoidTransactionDialog;
